import RatePlanStatusEnum from '@constants/enums/ratePlan/status';
import RatePlanTypeEnum from '@constants/enums/ratePlan/type';

import OrganisationApi from '@packages/organisations/api';
import normaliseRateSubscriptionResponse from '@packages/rate-plans/utils/normaliseRateSubscription';

import { wait } from '@utils/async';
import { download } from '@utils/file';

import {
    BillingGroup,
    Investor,
    InvestorAdviserRelationship,
    ProviderInvestor,
    RatePlan,
    Subscription
} from '@/models';
import Collection from '@/models/Collection';

export class InvestorApi extends OrganisationApi {
    static resource = 'investors';

    get endpoints() {
        return {
            ...super.endpoints,
            postClassification: this.postClassification.bind(this),
            applyForInvestmentAccount: this.applyForInvestmentAccount.bind(this),
            getAppropriateness: this.getAppropriateness.bind(this),
            postAppropriateness: this.postAppropriateness.bind(this),
            downloadAppropriateness: this.downloadAppropriateness.bind(this),
            getVisibility: this.getVisibility.bind(this),
            getBillingGroup: this.getBillingGroup.bind(this),
            getApplicationForm: this.getApplicationForm.bind(this),
            getAdviserRelationship: this.getAdviserRelationship.bind(this),
            postAdviserRelationship: this.postAdviserRelationship.bind(this),
            putAdviserRelationship: this.putAdviserRelationship.bind(this),
            patchAdviserRelationship: this.patchAdviserRelationship.bind(this),
            removeAdviserRelationship: this.removeAdviserRelationship.bind(this),
            postDriEnrollment: this.postDriEnrollment.bind(this),
            providers: this.providers.bind(this),
            products: this.products.bind(this),
            changeEmailAddress: this.changeEmailAddress.bind(this),
            withdrawCash: this.withdrawCash.bind(this),
            getProviderRelationships: this.getProviderRelationships.bind(this),
            getProviderRelationship: this.getProviderRelationship.bind(this),
            postProviderRelationship: this.postProviderRelationship.bind(this),
            putProviderRelationship: this.putProviderRelationship.bind(this),
            patchProviderRelationship: this.patchProviderRelationship.bind(this),
            removeProviderRelationship: this.removeProviderRelationship.bind(this),
            requestProviderLOA: this.requestProviderLOA.bind(this),
            downloadProviderLOA: this.downloadProviderLOA.bind(this),
            reinvestHoldings: this.reinvestHoldings.bind(this),
            getAdditionalNeeds: this.getAdditionalNeeds.bind(this),
            getAdditionalNeed: this.getAdditionalNeed.bind(this),
            postAdditionalNeed: this.postAdditionalNeed.bind(this),
            patchAdditionalNeed: this.patchAdditionalNeed.bind(this),
            removeAdditionalNeed: this.removeAdditionalNeed.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            getBillingGroup: this.getBillingGroup_mock.bind(this)
        };
    }

    _clearCollection(type) {
        type = type || this.model.name || null;
        if (!type) return;

        Collection.delete(collection => collection.type.includes(type));
        Collection.delete(collection => collection.type.includes('Client'));
    }

    _filterResponseData(data) {
        data = super._filterResponseData(data);

        if (data && data.id && data.adviser_accounts) {
            data.adviser_accounts = data.adviser_accounts.map(aa => {
                aa.investor_id = data.id;
                aa.adviser_id = aa.adviser.id;
                return aa;
            });
        }

        return data;
    }

    async postClassification(investorId, payload = {}) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/classifications`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection();
            this._clearCollection('Client');
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async applyForInvestmentAccount(investorId, payload = {}) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/apply`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection();
            this._clearCollection('Client');
            Investor.delete(investorId);
            InvestorAdviserRelationship.delete(item => item.investor_id === investorId);
        });
    }

    async getAppropriateness(investorId, config = {}) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/appropriateness`, {}, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            if (Array.isArray(response.data.questions)) {
                response.data.questions = response.data.questions.map(question => {
                    if (Array.isArray(question.answers)) {
                        question.answers = question.answers.map(answer => {
                            if (
                                !answer.text ||
                                (typeof answer.text === 'object' && Object.keys(answer.text).length === 0)
                            ) {
                                answer.text = null;
                            }
                            return answer;
                        });
                    }
                    return question;
                });
            }
        });
    }

    async postAppropriateness(investorId, questions = []) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/appropriateness`, {
            questions
        });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async downloadAppropriateness(investorId, blank = false) {
        return await OrganisationApi.api.get(
            `${this.base_url}/${investorId}/appropriateness/download`,
            blank ? { blank: true } : {},
            {
                headers: {
                    Accept: 'application/pdf'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );
    }

    async getVisibility(investorId, filters = {}, config = {}) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/visibility`, filters, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    _buildBillingGroupMockPayload(investorId, adviserId) {
        const billingGroupId = `billing-group-${investorId}`;
        const platformPlanId = `rate-plan-platform-${adviserId}`;
        const adviserPlanId = `rate-plan-adviser-${adviserId}`;

        return {
            id: billingGroupId,
            name: 'Mock Billing Group',
            active_subscriptions: [
                {
                    start_date: '2024-01-01',
                    end_date: null,
                    rate_plan_type: RatePlanTypeEnum.PLATFORM,
                    rate_plan: {
                        id: platformPlanId,
                        name: 'Default 0.25% AUM',
                        status: RatePlanStatusEnum.ACTIVE,
                        type: RatePlanTypeEnum.PLATFORM,
                        default: true,
                        adviser_id: adviserId
                    }
                },
                {
                    start_date: '2024-01-01',
                    end_date: null,
                    rate_plan_type: RatePlanTypeEnum.ADVISER,
                    rate_plan: {
                        id: adviserPlanId,
                        name: 'Default no fees',
                        status: RatePlanStatusEnum.ACTIVE,
                        type: RatePlanTypeEnum.ADVISER,
                        default: true,
                        adviser_id: adviserId
                    }
                }
            ]
        };
    }

    _persistBillingGroupResponse(response, investorId, adviserId) {
        const normalised = normaliseRateSubscriptionResponse(response.data, { investorId, adviserId });

        response.data = normalised;

        if (normalised.billing_group) {
            BillingGroup.insertOrUpdate({ data: normalised.billing_group });
        }

        if (normalised.subscriptions.length) {
            Subscription.insertOrUpdate({ data: normalised.subscriptions });
        }

        if (normalised.rate_plans.length) {
            RatePlan.insertOrUpdate({ data: normalised.rate_plans });
        }
    }

    async getBillingGroup(investorId, adviserId, config = {}) {
        if (!investorId) {
            throw new ReferenceError('investorId is required when requesting billing groups');
        }

        if (!adviserId) {
            throw new ReferenceError('adviserId is required when requesting billing groups');
        }

        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investorId}/billing-groups/${adviserId}`,
            {},
            config
        );

        return this._respond(response, () => {
            this._persistBillingGroupResponse(response, investorId, adviserId);
        });
    }

    async getBillingGroup_mock(investorId, adviserId) {
        if (!investorId) {
            throw new ReferenceError('investorId is required when requesting billing groups');
        }

        if (!adviserId) {
            throw new ReferenceError('adviserId is required when requesting billing groups');
        }

        await wait(200);

        const response = {
            status: 'success',
            data: this._buildBillingGroupMockPayload(investorId, adviserId)
        };

        return this._respond(response, () => {
            this._persistBillingGroupResponse(response, investorId, adviserId);
        });
    }

    async getApplicationForm(investorId, adviserId) {
        let url = `${this.base_url}`;

        if (investorId) {
            url += `/${investorId}`;
        }

        url += '/application-form';

        return await OrganisationApi.api.get(
            url,
            { adviser_id: adviserId },
            {
                headers: {
                    Accept: 'application/pdf'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );
    }

    async providers(offset = null, limit = null, sort_by = null, filters = {}, config = { base_url: null }) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            throw new Error(`investor_id is required when requesting providers for an investor.`);
        }

        let url = `${config.base_url || this.base_url}`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        url += '/providers';

        return this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });
    }

    async products(offset = null, limit = null, sort_by = null, filters = {}, config = { base_url: null }) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            throw new Error(`investor_id is required when requesting products for an investor.`);
        }

        let url = `${config.base_url || this.base_url}`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        url += '/products';

        return this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });
    }

    async getAdviserRelationship(investor_id, adviser_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async postAdviserRelationship(payload = {}) {
        const { investor_id, adviser_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on POST investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor) {
                const currentAdviserAccounts = Array.isArray(investor.adviser_accounts)
                    ? investor.adviser_accounts
                    : [];

                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: [...currentAdviserAccounts, response.data]
                    }
                });
            }
        });
    }

    async putAdviserRelationship(investor_id, adviser_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PUT investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on PUT investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.put(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor && Array.isArray(investor.adviser_accounts)) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: investor.adviser_accounts.map(aa => {
                            if (aa.adviser_id === adviser_id) {
                                return response.data;
                            }
                            return aa;
                        })
                    }
                });
            }
        });
    }

    async patchAdviserRelationship(investor_id, adviser_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on PATCH investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor && Array.isArray(investor.adviser_accounts)) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: investor.adviser_accounts.map(aa => {
                            if (aa.adviser_id === adviser_id) {
                                return response.data;
                            }
                            return aa;
                        })
                    }
                });
            }
        });
    }

    async removeAdviserRelationship(investor_id, adviser_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE investor/adviser relationship');
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on DELETE investor/adviser relationship');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`
        );

        return this._respond(response, () => {
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.delete(
                item => item.investor_id === investor_id && item.adviser_id === adviser_id
            );

            const investor = Investor.find(investor_id);

            if (investor) {
                const currentAdviserAccounts = Array.isArray(investor.adviser_accounts)
                    ? investor.adviser_accounts
                    : [];

                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: currentAdviserAccounts.filter(aa => aa?.adviser_id !== adviser_id)
                    }
                });
            }
        });
    }

    async postDriEnrollment(payload = {}) {
        const { investor_id, product_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST DRI enrollment', payload);
        }
        if (!product_id) {
            throw new Error('Missing product_id on POST DRI enrollment', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/products/${product_id}/dri`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async changeEmailAddress(investor_id, email_address = null) {
        if (!investor_id) {
            throw new Error('Missing investor_id on change email address');
        }
        if (!email_address) {
            throw new Error('Missing email_address on change email address');
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/change-email`, {
            email_address
        });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            const investor = Investor.find(investor_id);

            if (investor) {
                Investor.update({
                    where: investor_id,
                    data: { email: response.data.email }
                });
            }
        });
    }

    async withdrawCash(investor_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on cash withdrawal');
        }
        if (!payload.bank_account_id) {
            throw new Error('Missing bank_account_id on cash withdrawal');
        }
        if (
            !payload.cash_withdrawals ||
            !Array.isArray(payload.cash_withdrawals) ||
            payload.cash_withdrawals.length === 0
        ) {
            throw new Error('Missing cash_withdrawals on cash withdrawal');
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/cash-withdrawal`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    _filterProviderRelationshipResponseData(data) {
        if (!data) {
            return data;
        }

        const setId = item => ({
            id: `${item.investor?.id || item.investor_id || ProviderInvestor.default_investor_id}_${
                item.provider?.id || item.provider_id || ProviderInvestor.default_provider_id
            }`,
            ...item
        });

        if (Array.isArray(data)) {
            return data.map(item => setId(item));
        }

        return setId(data);
    }

    async getProviderRelationships(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            return {
                status: 'success',
                data: [],
                metadata: {
                    total: 0,
                    limit: 0,
                    offset: 0
                }
            };
        }

        let url = `${this.base_url}/${extract.value}/provider-relationships`;

        const response = await this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });

        if (Array.isArray(response.data)) {
            response.data = this._filterProviderRelationshipResponseData(response.data);
        }

        return response;
    }

    async getProviderRelationship(investor_id, provider_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async postProviderRelationship(payload = {}) {
        const { investor_id, provider_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on POST investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/provider-relationships`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async putProviderRelationship(investor_id, provider_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PUT investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on PUT investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.put(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async patchProviderRelationship(investor_id, provider_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on PATCH investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async removeProviderRelationship(investor_id, provider_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE investor/provider relationship');
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on DELETE investor/provider relationship');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`
        );

        return this._respond(response, () => {
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.delete(item => item.investor_id === investor_id && item.provider_id === provider_id);
        });
    }

    async requestProviderLOA(investor_id, provider_ids) {
        if (!investor_id) {
            throw new Error('Missing investor_id on request provider LOA');
        }

        if (!provider_ids) {
            throw new Error('Missing provider_ids on request provider LOA');
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/provider-relationships/request-loa`,
            {
                provider_ids
            }
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
        });
    }

    async downloadProviderLOA(investor_id, provider_ids, downloadFile = false) {
        if (!investor_id) {
            throw new Error('Missing investor_id on download provider LOA');
        }

        if (!provider_ids) {
            throw new Error('Missing provider_ids on download provider LOA');
        }

        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/provider-relationships/request-loa/download`,
            {
                provider_ids
            },
            {
                headers: {
                    Accept: 'application/pdf, application/zip'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );

        if (downloadFile) {
            const filename = response.request.getResponseHeader('x-filename');
            const name = filename || 'LOA';

            const reader = new FileReader();

            reader.onloadend = async () => {
                await download(reader.result, name);
            };

            reader.readAsDataURL(response.data);
        }

        return response;
    }

    async reinvestHoldings(payload = {}) {
        const { investor_id, holding_ids } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id while attempting to re-invest holdings', payload);
        }
        if (!holding_ids) {
            throw new Error('Missing holding_ids while attempting to re-invest holdings', payload);
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/holdings/reinvest`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getAdditionalNeeds(investorId, config = {}) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/additional-needs`, {}, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getAdditionalNeed(investor_id, additional_need_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async postAdditionalNeed(investor_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on POST additional need', payload);
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/additional-needs`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];

                const existingIndex = additional_needs.findIndex(
                    need => need.id === response.data.id || need.type === response.data.type
                );

                if (existingIndex > -1) {
                    additional_needs[`${existingIndex}`] = response.data;
                } else {
                    additional_needs.push(response.data);
                }

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }

    async patchAdditionalNeed(investor_id, additional_need_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH additional need', payload);
        }
        if (!additional_need_id) {
            throw new Error('Missing additional_need_id on PATCH additional need', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];

                const existingIndex = additional_needs.findIndex(
                    need => need.id === response.data.id || need.type === response.data.type
                );

                if (existingIndex > -1) {
                    additional_needs[`${existingIndex}`] = response.data;
                } else {
                    additional_needs.push(response.data);
                }

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }

    async removeAdditionalNeed(investor_id, additional_need_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE additional need');
        }
        if (!additional_need_id) {
            throw new Error('Missing additional_need_id on DELETE additional need');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`
        );

        return this._respond(response, () => {
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];
                additional_needs = additional_needs.filter(need => need.id !== additional_need_id);

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }
}

export default InvestorApi;
